import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import { apiFetch, apiDownload } from '../../lib/api';
import {
  Dipendente, DocumentoDipendente, Societa,
  StatoDipendente, TipoDocumentoDipendente, ScadenzaFlag,
} from '../../types';

const STATO_BADGE: Record<StatoDipendente, string> = {
  ATTIVO: 'bop-badge--success', ASPETTATIVA: 'bop-badge--warning', CESSATO: 'bop-badge--neutral',
};
const SCADENZA_BADGE: Record<ScadenzaFlag, string> = {
  VERDE: 'bop-badge--success', GIALLO: 'bop-badge--warning', ARANCIO: 'bop-badge--warning', ROSSO: 'bop-badge--danger',
};
const TIPI_DOC: TipoDocumentoDipendente[] = [
  'PROPOSTA','LETTERA_ASSUNZIONE','CONTRATTO','DOCUMENTO_IDENTITA','CERTIFICATO','ALTRO',
];
const TIPI_DOC_LABEL: Record<TipoDocumentoDipendente, string> = {
  PROPOSTA: 'Proposta', LETTERA_ASSUNZIONE: 'Lettera assunzione', CONTRATTO: 'Contratto',
  DOCUMENTO_IDENTITA: 'Documento identità', CERTIFICATO: 'Certificato', ALTRO: 'Altro',
};

type Tab = 'anagrafica' | 'documenti' | 'storico';

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const isoDate = (d?: string | null) => d ? d.split('T')[0] : '';
const EMPTY_FORM = {
  societaId: '', nome: '', cognome: '', codiceFiscale: '',
  dataNascita: '', residenza: '', telefono: '', email: '',
  dataAssunzione: '', tipoContratto: 'INDETERMINATO', dataFineContratto: '',
  mansione: '', ccnl: '', ral: '',
};

export default function SchedaDipendente() {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isNew     = !id || id === 'nuovo';
  const prefill   = (location.state as any)?.prefill;

  const [dipendente, setDipendente] = useState<Dipendente | null>(null);
  const [societa,    setSocieta]    = useState<Societa[]>([]);
  const [loading,    setLoading]    = useState(!isNew);
  const [error,      setError]      = useState('');
  const [saving,     setSaving]     = useState(false);
  const [tab,        setTab]        = useState<Tab>('anagrafica');
  const [editing,    setEditing]    = useState(isNew);
  const [form,       setForm]       = useState({ ...EMPTY_FORM, ...(prefill || {}) });

  // Stato modal
  const [showStatoModal,  setShowStatoModal]  = useState(false);
  const [nuovoStato,      setNuovoStato]      = useState<StatoDipendente>('ATTIVO');
  const [dataCessazione,  setDataCessazione]  = useState('');
  const [causaleCessazione, setCausaleCessazione] = useState('');
  const [savingStato,     setSavingStato]     = useState(false);

  // Upload documento modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTipo,      setUploadTipo]      = useState<TipoDocumentoDipendente>('CONTRATTO');
  const [uploadScadenza,  setUploadScadenza]  = useState('');
  const [uploadFile,      setUploadFile]      = useState<File | null>(null);
  const [uploading,       setUploading]       = useState(false);
  const [uploadError,     setUploadError]     = useState('');

  const f = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((p: typeof EMPTY_FORM) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    apiFetch<{ societa: Societa[] }>('/api/societa').then((d) => setSocieta(d.societa)).catch(() => {});
    if (!isNew) {
      apiFetch<{ dipendente: Dipendente }>(`/api/dipendenti/${id}`)
        .then((d) => {
          setDipendente(d.dipendente);
          setForm({
            societaId:       String(d.dipendente.societaId),
            nome:            d.dipendente.nome,
            cognome:         d.dipendente.cognome,
            codiceFiscale:   d.dipendente.codiceFiscale,
            dataNascita:     isoDate(d.dipendente.dataNascita),
            residenza:       d.dipendente.residenza || '',
            telefono:        d.dipendente.telefono || '',
            email:           d.dipendente.email || '',
            dataAssunzione:  isoDate(d.dipendente.dataAssunzione),
            tipoContratto:   d.dipendente.tipoContratto,
            dataFineContratto: isoDate(d.dipendente.dataFineContratto),
            mansione:        d.dipendente.mansione || '',
            ccnl:            d.dipendente.ccnl || '',
            ral:             d.dipendente.ral != null ? String(d.dipendente.ral) : '',
          });
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [id]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const body = {
        ...form,
        societaId:        parseInt(form.societaId),
        ral:              form.ral ? parseFloat(form.ral) : null,
        dataFineContratto: form.dataFineContratto || null,
        candidatoId:      prefill?.candidatoId || undefined,
      };
      if (isNew) {
        const d = await apiFetch<{ dipendente: Dipendente }>('/api/dipendenti', { method: 'POST', body: JSON.stringify(body) });
        navigate(`/dipendenti/${d.dipendente.id}`, { replace: true });
      } else {
        const d = await apiFetch<{ dipendente: Dipendente }>(`/api/dipendenti/${id}`, { method: 'PUT', body: JSON.stringify(body) });
        setDipendente(d.dipendente); setEditing(false);
      }
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleCambiaStato = async () => {
    setSavingStato(true);
    try {
      const d = await apiFetch<{ dipendente: Dipendente }>(`/api/dipendenti/${id}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato: nuovoStato, dataCessazione: dataCessazione || null, causaleCessazione: causaleCessazione || null }),
      });
      setDipendente(d.dipendente); setShowStatoModal(false);
    } catch (e: any) { setError(e.message); }
    finally { setSavingStato(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) { setUploadError('Seleziona un file'); return; }
    setUploading(true); setUploadError('');
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('tipo', uploadTipo);
    if (uploadScadenza) fd.append('dataScadenza', uploadScadenza);
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = (await import('../../config.js')).default;
      const res = await fetch(`${BACKEND_URL}/api/dipendenti/${id}/documenti`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.error || `Errore ${res.status}`); }
      const data: { documento: DocumentoDipendente } = await res.json();
      setDipendente((prev) => prev ? { ...prev, documenti: [data.documento, ...(prev.documenti || [])] } : prev);
      setShowUploadModal(false); setUploadFile(null); setUploadScadenza('');
    } catch (e: any) { setUploadError(e.message); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId: number) => {
    if (!window.confirm('Eliminare il documento?')) return;
    try {
      await apiFetch(`/api/dipendenti/${id}/documenti/${docId}`, { method: 'DELETE' });
      setDipendente((prev) => prev ? { ...prev, documenti: (prev.documenti || []).filter((d) => d.id !== docId) } : prev);
    } catch (e: any) { setError(e.message); }
  };

  if (loading) return <Layout><div className="inline-loading">Caricamento…</div></Layout>;

  const docs = dipendente?.documenti || [];
  const docsByTipo = TIPI_DOC.reduce((acc, tipo) => {
    acc[tipo] = docs.filter((d) => d.tipo === tipo);
    return acc;
  }, {} as Record<TipoDocumentoDipendente, DocumentoDipendente[]>);

  return (
    <Layout title={isNew ? 'Nuovo Dipendente' : `${dipendente?.cognome} ${dipendente?.nome}`}>
      {/* Header */}
      <BopBackLink to="/aziendale/dipendenti" label="Torna ai dipendenti" />
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>{isNew ? 'Nuovo Dipendente' : `${dipendente?.cognome} ${dipendente?.nome}`}</h2>
          {!isNew && dipendente && (
            <p>{dipendente.mansione || ''}{dipendente.societaNome ? ` — ${dipendente.societaNome}` : ''}</p>
          )}
        </div>
        <div className="bop-scheda-dip__header-actions">
          {!isNew && dipendente && (
            <span className={`bop-badge ${STATO_BADGE[dipendente.stato]} bop-scheda-dip__badge-stato`}>{dipendente.stato}</span>
          )}
          {!isNew && !editing && (
            <>
              <button className="bop-btn bop-btn-secondary" onClick={() => setEditing(true)}>Modifica</button>
              <button className="bop-btn bop-btn-secondary" onClick={() => { setNuovoStato(dipendente?.stato || 'ATTIVO'); setShowStatoModal(true); }}>
                Cambia Stato
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="inline-error">{error}</div>}

      {/* Tabs — solo se non è nuovo */}
      {!isNew && (
        <div className="bop-tabs">
          {(['anagrafica', 'documenti', 'storico'] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`bop-tab ${tab === t ? 'active' : ''}`}>
              {t === 'anagrafica' ? 'Anagrafica' : t === 'documenti' ? 'Documenti' : 'Storico'}
            </button>
          ))}
        </div>
      )}

      {/* ── TAB ANAGRAFICA ────────────────────────────────────────── */}
      {(isNew || tab === 'anagrafica') && (
        <div className="bop-card bop-scheda-dip__card-body">
          {editing ? (
            <>
              <div className="bop-form-row bop-scheda-dip__form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Società <span className="bop-text-danger">*</span></label>
                  <select className="bop-input" value={form.societaId} onChange={f('societaId')}>
                    <option value="">— Seleziona —</option>
                    {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Mansione</label>
                  <input className="bop-input" value={form.mansione} onChange={f('mansione')} />
                </div>
              </div>
              <div className="bop-form-row bop-scheda-dip__form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Nome <span className="bop-text-danger">*</span></label>
                  <input className="bop-input" value={form.nome} onChange={f('nome')} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome <span className="bop-text-danger">*</span></label>
                  <input className="bop-input" value={form.cognome} onChange={f('cognome')} />
                </div>
              </div>
              <div className="bop-form-row bop-scheda-dip__form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Codice Fiscale <span className="bop-text-danger">*</span></label>
                  <input className="bop-input bop-scheda-dip__input-uppercase" value={form.codiceFiscale} onChange={f('codiceFiscale')} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data di nascita <span className="bop-text-danger">*</span></label>
                  <input type="date" className="bop-input" value={form.dataNascita} onChange={f('dataNascita')} />
                </div>
              </div>
              <div className="bop-form-row bop-scheda-dip__form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Email</label>
                  <input type="email" className="bop-input" value={form.email} onChange={f('email')} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Telefono</label>
                  <input className="bop-input" value={form.telefono} onChange={f('telefono')} />
                </div>
              </div>
              <div className="bop-form-group bop-scheda-dip__form-row">
                <label className="bop-label">Residenza</label>
                <input className="bop-input" value={form.residenza} onChange={f('residenza')} />
              </div>
              <hr className="bop-scheda-dip__divider" />
              <div className="bop-form-row bop-scheda-dip__form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Data assunzione <span className="bop-text-danger">*</span></label>
                  <input type="date" className="bop-input" value={form.dataAssunzione} onChange={f('dataAssunzione')} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Tipo contratto <span className="bop-text-danger">*</span></label>
                  <select className="bop-input" value={form.tipoContratto} onChange={f('tipoContratto')}>
                    <option value="INDETERMINATO">Indeterminato</option>
                    <option value="DETERMINATO">Determinato</option>
                    <option value="STAGE">Stage</option>
                  </select>
                </div>
              </div>
              {form.tipoContratto !== 'INDETERMINATO' && (
                <div className="bop-form-group bop-scheda-dip__form-row">
                  <label className="bop-label">Data fine contratto</label>
                  <input type="date" className="bop-input" value={form.dataFineContratto} onChange={f('dataFineContratto')} />
                </div>
              )}
              <div className="bop-form-row bop-scheda-dip__form-row">
                <div className="bop-form-group">
                  <label className="bop-label">CCNL</label>
                  <input className="bop-input" value={form.ccnl} onChange={f('ccnl')} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">RAL (€)</label>
                  <input type="number" className="bop-input" value={form.ral} onChange={f('ral')} />
                </div>
              </div>
              <div className="bop-scheda-dip__form-actions">
                <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvataggio…' : isNew ? 'Crea Dipendente' : 'Salva modifiche'}
                </button>
                {!isNew && <button className="bop-btn bop-btn-secondary" onClick={() => setEditing(false)}>Annulla</button>}
              </div>
            </>
          ) : (
            // View mode
            <div className="bop-scheda-dip__detail-grid">
              {[
                ['Cognome e Nome', `${dipendente?.cognome} ${dipendente?.nome}`],
                ['Codice Fiscale', dipendente?.codiceFiscale],
                ['Data di nascita', fmtDate(dipendente?.dataNascita)],
                ['Email', dipendente?.email || '—'],
                ['Telefono', dipendente?.telefono || '—'],
                ['Residenza', dipendente?.residenza || '—'],
                ['Società', dipendente?.societaNome || '—'],
                ['Mansione', dipendente?.mansione || '—'],
                ['Data assunzione', fmtDate(dipendente?.dataAssunzione)],
                ['Tipo contratto', dipendente?.tipoContratto],
                ['Data fine contratto', fmtDate(dipendente?.dataFineContratto)],
                ['CCNL', dipendente?.ccnl || '—'],
                ['RAL', dipendente?.ral != null ? `€ ${Number(dipendente.ral).toLocaleString('it-IT')}` : '—'],
                ['Data cessazione', fmtDate(dipendente?.dataCessazione)],
                ['Causale cessazione', dipendente?.causaleCessazione || '—'],
              ].map(([label, value], i) => (
                <div key={i}>
                  <div className="bop-scheda-dip__detail-label">{label}</div>
                  <div className="bop-scheda-dip__detail-value">{value as string}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB DOCUMENTI ─────────────────────────────────────────── */}
      {!isNew && tab === 'documenti' && (
        <>
          <div className="bop-scheda-dip__doc-header">
            <button className="bop-btn bop-btn-primary" onClick={() => { setUploadError(''); setShowUploadModal(true); }}>
              + Carica Documento
            </button>
          </div>
          {TIPI_DOC.filter((tipo) => docsByTipo[tipo].length > 0).map((tipo) => (
            <div className="bop-card bop-scheda-dip__doc-card" key={tipo}>
              <div className="bop-scheda-dip__doc-tipo-header">
                <strong className="bop-scheda-dip__doc-tipo-label">{TIPI_DOC_LABEL[tipo]}</strong>
              </div>
              <div className="bop-table-wrapper">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Nome file</th>
                      <th>Caricato il</th>
                      <th>Scadenza</th>
                      <th className="bop-scheda-dip__th-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {docsByTipo[tipo].map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.nome}</td>
                        <td className="bop-scheda-dip__td-date">{new Date(doc.uploadedAt).toLocaleDateString('it-IT')}</td>
                        <td>
                          {doc.dataScadenza ? (
                            <>
                              {fmtDate(doc.dataScadenza)}
                              {doc.scadenzaFlag && (
                                <span className={`bop-badge ${SCADENZA_BADGE[doc.scadenzaFlag]} bop-scheda-dip__badge-scadenza`}>
                                  {doc.scadenzaFlag}
                                </span>
                              )}
                            </>
                          ) : '—'}
                        </td>
                        <td>
                          <div className="bop-scheda-dip__doc-actions">
                            <button className="bop-btn bop-btn-ghost bop-btn-sm"
                              onClick={() => apiDownload(`/api/dipendenti/${id}/documenti/${doc.id}/download`, doc.nome)}>
                              ↓ Scarica
                            </button>
                            <button className="bop-btn bop-btn-danger bop-btn-sm" onClick={() => handleDeleteDoc(doc.id)}>
                              Elimina
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {docs.length === 0 && <div className="bop-card"><div className="bop-empty-state">Nessun documento caricato.</div></div>}
        </>
      )}

      {/* ── TAB STORICO ───────────────────────────────────────────── */}
      {!isNew && tab === 'storico' && dipendente && (
        <div className="bop-card bop-scheda-dip__card-body">
          <div className="bop-scheda-dip__timeline">
            <div className="bop-scheda-dip__timeline-line" />
            {[
              { data: dipendente.dataAssunzione, label: 'Assunzione', sub: dipendente.tipoContratto },
              dipendente.dataCessazione ? { data: dipendente.dataCessazione, label: 'Cessazione', sub: dipendente.causaleCessazione || '' } : null,
            ].filter(Boolean).map((ev, i) => (
              <div key={i} className="bop-scheda-dip__timeline-item">
                <div className="bop-scheda-dip__timeline-dot" />
                <div className="bop-scheda-dip__timeline-date">{fmtDate(ev!.data)}</div>
                <div className="bop-scheda-dip__timeline-label">{ev!.label}</div>
                {ev!.sub && <div className="bop-scheda-dip__timeline-sub">{ev!.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Modal Cambia Stato ────────────────────────────────────── */}
      {showStatoModal && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Cambia Stato</h3>
              <button className="bop-modal__close" onClick={() => setShowStatoModal(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Nuovo stato</label>
                <select className="bop-input" value={nuovoStato} onChange={(e) => setNuovoStato(e.target.value as StatoDipendente)}>
                  <option value="ATTIVO">Attivo</option>
                  <option value="ASPETTATIVA">Aspettativa</option>
                  <option value="CESSATO">Cessato</option>
                </select>
              </div>
              {nuovoStato === 'CESSATO' && (
                <>
                  <div className="bop-form-group">
                    <label className="bop-label">Data cessazione</label>
                    <input type="date" className="bop-input" value={dataCessazione} onChange={(e) => setDataCessazione(e.target.value)} />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Causale</label>
                    <input className="bop-input" value={causaleCessazione} onChange={(e) => setCausaleCessazione(e.target.value)} placeholder="Es. Dimissioni volontarie" />
                  </div>
                </>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowStatoModal(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={handleCambiaStato} disabled={savingStato}>
                {savingStato ? 'Salvataggio…' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Upload Documento ────────────────────────────────── */}
      {showUploadModal && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Carica Documento</h3>
              <button className="bop-modal__close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Tipo documento</label>
                <select className="bop-input" value={uploadTipo} onChange={(e) => setUploadTipo(e.target.value as TipoDocumentoDipendente)}>
                  {TIPI_DOC.map((t) => <option key={t} value={t}>{TIPI_DOC_LABEL[t]}</option>)}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data scadenza (opzionale)</label>
                <input type="date" className="bop-input" value={uploadScadenza} onChange={(e) => setUploadScadenza(e.target.value)} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">File (PDF, JPG, PNG — max 10 MB) <span className="bop-text-danger">*</span></label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
                {uploadFile && <div className="bop-scheda-dip__upload-filename">{uploadFile.name}</div>}
              </div>
              {uploadError && <div className="inline-error">{uploadError}</div>}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowUploadModal(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Caricamento…' : 'Carica'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
